import { ReactElement, ReactNode } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from 'app/components/ui/dropdown-menu'
import { useListCopy } from 'app/hooks/useListCopy'
import type { PipelineType } from 'app/types/entities'

// The dynamic `border-pipelines-*` classes are generated centrally via the
// `@source inline(...)` directive in src/styles/tailwind.css — no per-file safelist needed.

interface AddPipelineMenuProps {
  /** Pipelines the contact can still join (caller filters out current memberships). */
  pipelines: PipelineType[]
  onAdd: (pipelineId: string, stageName: string) => void
  triggerClassName?: string
  /** Accessible name for the trigger (its visible text may be just "+"). */
  triggerAriaLabel?: string
  /** Trigger content (a "+" chip, an italic link, ...). */
  children: ReactNode
}

/**
 * Nested add-to-pipeline menu for the contacts-table pipelines cell: top level
 * lists joinable pipelines, each with a submenu of its stages. Selecting a stage
 * adds the membership in one interaction — no modal.
 */
export function AddPipelineMenu({
  pipelines,
  onAdd,
  triggerClassName,
  triggerAriaLabel,
  children,
}: AddPipelineMenuProps): ReactElement {
  const copy = useListCopy()
  return (
    <DropdownMenu modal={false}>
      {/* stopPropagation: inside a table row the row's own click handler must not fire. */}
      <DropdownMenuTrigger
        className={triggerClassName}
        aria-label={triggerAriaLabel}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48 bg-surface-raised" onClick={(e) => e.stopPropagation()}>
        {pipelines.length === 0 ? (
          <DropdownMenuItem disabled className="text-body-xs text-subtle">
            {copy.allListsAdded}
          </DropdownMenuItem>
        ) : (
          pipelines.map((p) => (
            <DropdownMenuSub key={p.id}>
              <DropdownMenuSubTrigger className="text-body-xs text-default">
                <span className="truncate">{p.name}</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-48 bg-surface-raised">
                {[...(p.stages ?? [])]
                  .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                  .map((s) => (
                    <DropdownMenuItem
                      key={s.name}
                      onSelect={() => onAdd(p.id!, s.name)}
                      className="flex items-center gap-2 text-body-xs text-default"
                    >
                      <span className={`h-2 w-2 shrink-0 rounded-[2px] border-2 border-${s.color || `strong`}`} />
                      <span className="truncate">{s.name}</span>
                    </DropdownMenuItem>
                  ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
